export type Mode = "descent" | "freeride" | "timeTrial" | "jumpPark";
export type CourseId = 1 | 2 | 3;
export type Grade = "green" | "red" | "black";
export type ZoneId = "summit" | "scree" | "meadow" | "forest" | "valley";
export type Surface = "dirt" | "grass" | "mud" | "gravel" | "roots" | "rock" | "snow" | "ice" | "wood";
export type Severity = "small" | "medium" | "large";

export const MODES: Mode[] = ["descent", "freeride", "timeTrial", "jumpPark"];
export const ZONES: ZoneId[] = ["summit", "scree", "meadow", "forest", "valley"];
export const SURFACES: Surface[] = ["dirt", "grass", "mud", "gravel", "roots", "rock", "snow", "ice", "wood"];
export const GRADES: Grade[] = ["green", "red", "black"];

export interface PlayerConfig {
    startSpeed: number;
    maxSpeed: number;
    boostSpeed: number;
    boostAccel: number;
    gravityAccel: number;
    pedalAccel: number;
    pedalCapBonus: number;
    braking: number;
    skidThreshold: number;
    skidBraking: number;
    skidSteerFactor: number;
    turnRate: number;
    headingSmoothing: number;
    traverseFriction: number;
    minRollSpeed: number;
    overCapDecay: number;
    sharpTurnLoss: number;
    boostDrainPerS: number;
    boostRefillCleanLanding: number;
    boostRefillPerTrickPoint: number;
    boostPassiveRefillPerS: number;
    crashDurationS: Record<Severity, number>;
    invulnerableS: number;
}

export interface ObstacleConfig {
    kind: "solid" | "soft" | "region" | "decor" | "ramp";
    hitboxPx?: [number, number];
    heightPx?: number;
    tall?: boolean;
    surface?: Surface;
    crashAtSpeed?: { medium: number; large: number };
    speedLoss?: number;
    hopSpeed?: number;
    gripBonus?: number;
    launchScale?: number;
    lipPx?: number;
    tablePx?: number;
    dropM?: number;
}

export interface SurfaceConfig {
    grip: number;
    speedMod: number;
    drag: number;
    rough: number;
    turnLoss?: number;
    spray: string;
    sound: string;
    haptic: string;
}

export interface SpawnEntry {
    key: string;
    weight: number;
    variant?: string;
}

export interface ZoneConfig {
    groundColor: string;
    marksKey: string | null;
    baseSurface: Surface;
    trail: { surface: Surface; width: number; wiggle: number } | null;
    spawnTable: SpawnEntry[];
    setPieces: string[];
    ambience: string;
}

export interface ZonesFile {
    zones: Record<ZoneId, ZoneConfig>;
    surfaces: Record<Surface, SurfaceConfig>;
}

export interface ZoneLeg {
    zone: ZoneId;
    lengthM: number;
}

export interface CourseConfig {
    id: CourseId;
    name: string;
    grade: Grade;
    seed: number;
    legs: ZoneLeg[];
    bigfoot: { zone: ZoneId; offsetM: number; aggression: number; scare: boolean };
    finish: "banner" | "village";
}

export interface CoursesFile {
    courses: CourseConfig[];
    freeride: { cycle: ZoneLeg[]; bigfootAtM: number; aggression: number };
}

export interface DifficultyParams {
    treeDensity: number;
    rockDensity: number;
    dogDensity: number;
    hikerDensity: number;
    npcRiderDensity: number;
    wildlifeDensity: number;
    jumpFrequency: number;
    regionFrequency: number;
    gateFrequency: number;
    pickupFrequency: number;
    setPieceWeight: number;
    bigfootAggression: number;
    scale: number;
}

export type DensityParams = Omit<DifficultyParams, "bigfootAggression" | "scale">;

export interface DifficultyFile {
    zoneBase: Record<ZoneId, DensityParams>;
    gradeMultiplier: Record<Grade, number>;
    zoneRamp: { start: number; end: number; transitionDip: number };
    freeride: { bands: { fromM: number; scale: number }[]; softCapRate: number; softCapExtra: number };
    caps: DensityParams;
}

export interface TrickConfig {
    value: number;
}

export interface TricksFile {
    tricks: Record<string, TrickConfig>;
    trickDuration: number;
    spin180Value: number;
    cleanLandingAngle: number;
    air: {
        gravity: number;
        launchPerSpeed: number;
        bunnyhopVz: number;
        bunnyhopCooldownS: number;
        popWindowS: number;
        popBonus: number;
        airTurnFactor: number;
        dropVzFactor: number;
        spinDegPerS: number;
        bigAirS: number;
        cleanLandSpeedBonus: number;
        tableShortLoss: number;
        mudLandLoss: number;
    };
}

export interface ScoringConfig {
    distancePerM: number;
    speedBonus: { fromSpeed: number; toSpeed: number; maxMultiplier: number };
    comboStep: number;
    comboCap: number;
    failedLanding: number;
    crashPenalty: Record<Severity, number>;
    escapeBonus: number;
}

export interface ModeConfig {
    distanceScore: boolean;
    bigfoot: boolean;
    gates: number;
    gateMissPenaltyS: number;
    pickups: boolean;
    severeCrashEnds: boolean;
    difficultyScale: Partial<DensityParams>;
}

export interface ModesFile {
    modes: Record<Mode, ModeConfig>;
    severeCrash: { count: number; windowS: number };
}

export interface HazardBase {
    hitboxPx: [number, number];
    heightPx: number;
    crashAtSpeed: { medium: number; large: number };
    speedLoss: number;
}

export interface DogConfig extends HazardBase {
    triggerM: number;
    alertS: number;
    speed: number;
    chaseS: number;
    leadS: number;
    fleeSpeed: number;
    tumbleS: number;
}

export interface HikerConfig extends HazardBase {
    walkSpeed: number;
    startleM: number;
    leapM: number;
    leapS: number;
    bellRangeM: number;
    asideSpeed: number;
    clearMarginM: number;
}

export interface NpcRiderConfig extends HazardBase {
    speed: number;
    lookAheadM: number;
    laneM: number;
    clearanceM: number;
    turnRate: number;
    fallenS: number;
}

export type Species = "cow" | "sheep" | "deer" | "marmot";

export interface AnimalConfig extends HazardBase {
    speed: number;
    boltSpeed: number;
    boltM: number;
}

export interface HazardsFile {
    dog: DogConfig;
    hiker: HikerConfig;
    npc_rider: NpcRiderConfig;
    wildlife: Record<Species, AnimalConfig>;
}

export interface GameConfig {
    player: PlayerConfig;
    obstacles: Record<string, ObstacleConfig>;
    zones: ZonesFile;
    courses: CoursesFile;
    difficulty: DifficultyFile;
    tricks: TricksFile;
    modes: ModesFile;
    scoring: ScoringConfig;
    hazards: HazardsFile;
}

export interface EntityDescriptor {
    type: string;
    x: number;
    y: number;
    variant?: string;
}
